import React, { useEffect, useState,useMemo } from "react";
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Popconfirm,
  message,
  Empty,
} from "antd";
import { ShopOutlined, PlusOutlined } from "@ant-design/icons";

//  API IMPORT
import {
  getVendors,
  createVendor,
  updateVendor,
  deleteVendor,
} from "../../api/vendorApi";
import {
  canViewModule,
  canCreateModule,
  canUpdateModule,
  canDeleteModule,
} from "../utils/permissionUtils";

const { Option } = Select;
const MODULE_KEY = "vendor";

const Vendor = () => {
  const hasViewAccess = canViewModule(MODULE_KEY);
  const [vendors, setVendors] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const getVendorId = (record = {}) => record?.id ?? record?.vendor_id ?? null;

  const normalizeVendor = (vendor = {}) => ({
    ...vendor,
    id: getVendorId(vendor),
    gst_number: vendor?.gst_number ?? vendor?.gst ?? "",
  });

  // ---------------- FETCH DATA ----------------
  const fetchVendors = async () => {
    try {
      setLoading(true);
      const res = await getVendors();
      const list = Array.isArray(res?.data) ? res.data : [];
      setVendors(list.map(normalizeVendor));
    } catch (err) {
      console.error("FETCH ERROR:", err);
      message.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasViewAccess) return;
    fetchVendors();
  }, [hasViewAccess]);

  const filteredVendors = useMemo(() => {
  if (!searchText) return vendors;

  return vendors.filter((v) =>
    (v.name || "").toLowerCase().includes(searchText.toLowerCase()) ||
    (v.phone || "").toLowerCase().includes(searchText.toLowerCase()) ||
    (v.category || "").toLowerCase().includes(searchText.toLowerCase())
  );
}, [vendors, searchText]);

  // ---------------- OPEN DRAWER ----------------
  const openDrawer = (record = null) => {
    if (record && !canUpdateModule(MODULE_KEY)) {
      message.error("No permission to update vendor");
      return;
    }

    if (!record && !canCreateModule(MODULE_KEY)) {
      message.error("No permission to create vendor");
      return;
    }

    setEditingVendor(record);
    setIsDrawerOpen(true);

    if (record) {
      form.setFieldsValue(normalizeVendor(record));
    } else {
      form.resetFields();
    }
  };

  // ---------------- CLOSE DRAWER ----------------
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingVendor(null);
    form.resetFields();
  };

  // ---------------- SAVE ----------------
  const handleSave = async (values) => {
    if (editingVendor) {
      if (!canUpdateModule(MODULE_KEY)) {
        message.error("No permission to update vendor");
        return;
      }
    } else if (!canCreateModule(MODULE_KEY)) {
      message.error("No permission to create vendor");
      return;
    }

    try {
      const payload = {
        name: values?.name || "",
        phone: values?.phone || "",
        address: values?.address || "",
        category: values?.category || "",
        pan: values?.pan || "",
        aadhar: values?.aadhar || "",
        gst_number: values?.gst_number || "",
        status: values?.status || "Active",
      };

      if (editingVendor) {
        const vendorId = getVendorId(editingVendor);
        if (!vendorId) {
          message.error("Vendor ID missing. Please refresh and try again.");
          return;
        }
        await updateVendor(vendorId, payload);
        message.success("Vendor updated successfully");
      } else {
        await createVendor(payload);
        message.success("Vendor created successfully");
      }

      await fetchVendors();
      closeDrawer();
    } catch (err) {
      console.error("SAVE ERROR:", err);
      message.error(err?.response?.data?.error || "Failed to save vendor");
    }
  };

  // ---------------- DELETE ----------------
  const handleDelete = async (id) => {
    if (!canDeleteModule(MODULE_KEY)) {
      message.error("No permission to delete vendor");
      return;
    }

    if (!id) {
      message.error("Vendor ID missing");
      return;
    }

    try {
      await deleteVendor(id);
      message.success("Vendor deleted successfully");
      fetchVendors();
    } catch (err) {
      console.error("DELETE ERROR:", err);
      message.error(err?.response?.data?.error || "Failed to delete vendor");
    }
  };

  // ---------------- TABLE ----------------
  const columns = [
    { title: "Vendor Name", dataIndex: "name" },
    { title: "Phone", dataIndex: "phone" },
    { title: "Address", dataIndex: "address" },
    { title: "Supply Category", dataIndex: "category" },
    { title: "PAN", dataIndex: "pan" },
    { title: "Aadhaar", dataIndex: "aadhar" },
    { title: "GST", dataIndex: "gst_number" },
    { title: "Status", dataIndex: "status" },

    {
      title: "Action",
      render: (_, record) => (
        <>
          <Button
            type="link"
            onClick={() => openDrawer(record)}
            disabled={!canUpdateModule(MODULE_KEY)}
          >
            Edit
          </Button>

          <Popconfirm
            title="Delete vendor?"
            onConfirm={() => handleDelete(getVendorId(record))}
          >
            <Button type="link" danger disabled={!canDeleteModule(MODULE_KEY)}>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  if (!hasViewAccess) {
    return (
      <div className="page-shell user-master-page">
        <div className="page-empty">
          <Empty description="No permission to view vendor page" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell user-master-page">
      {/* HEADER */}
      <div className="user-page-header">
        <div className="user-page-header-left">
          <div className="user-page-icon">
            <ShopOutlined />
          </div>

          <div>
            <h2 className="user-page-title">Vendor Management</h2>
            <p className="user-page-subtitle">
              Manage all vendors (Rice, Water, Vegetables etc).
            </p>
          </div>
        </div>
         
          <div className="user-page-header-actions">
        <Input.Search
          placeholder="Search vendor..."
          allowClear
          style={{ width: 250 }}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openDrawer()}
          disabled={!canCreateModule(MODULE_KEY)}
        >
          Create Vendor
        </Button>
      </div>
      </div>

      {/* TABLE */}
      <Table
        dataSource={vendors}
        columns={columns}
        rowKey={(record) => String(getVendorId(record))}
        loading={loading}
      />

      {/* DRAWER FORM */}
      <Drawer
        title={editingVendor ? "Edit Vendor" : "Add Vendor"}
        width={420}
        onClose={closeDrawer}
        open={isDrawerOpen}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="name"
            label="Vendor Name"
            rules={[{ required: true, message: "Enter vendor name" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>

          <Form.Item name="address" label="Address">
            <Input />
          </Form.Item>

          <Form.Item name="category" label="Vendor Type">
            <Select placeholder="Select type">
              <Option value="Rice">Rice</Option>
              <Option value="Drinks">Drinks</Option>
              <Option value="Vegetables">Vegetables</Option>
              <Option value="Dairy">Dairy</Option>
              <Option value="Meat">Meat</Option>
              <Option value="General">General</Option>
            </Select>
          </Form.Item>

          <Form.Item name="pan" label="PAN">
            <Input placeholder="ABCDE1234F" />
          </Form.Item>

          <Form.Item name="aadhar" label="Aadhaar">
            <Input placeholder="12-digit Aadhaar" />
          </Form.Item>

          <Form.Item name="gst_number" label="GST ">
            <Input placeholder="GST" />
          </Form.Item>

          <Form.Item name="status" label="Status" initialValue="Active">
            <Select>
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
          </Form.Item>

          <div style={{ display: "flex", gap: "10px" }}>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              disabled={
                editingVendor
                  ? !canUpdateModule(MODULE_KEY)
                  : !canCreateModule(MODULE_KEY)
              }
            >
              {editingVendor ? "Update Vendor" : "Save Vendor"}
            </Button>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default Vendor;
